import React, { useMemo } from "react";
import styled from "styled-components";
import { Icon, IconContainer } from "../visuals/styledComponents";
import { RowContainer } from "../containers/styledComponents";
import { CompassParagraph } from "./styledComponents";
import { SideMenuItem } from "./SideMenuItem";
import { greyText } from "../../styles";
import {
  SIDEMENU_ITEMS,
  BOTTOM_SIDEMENU_ITEMS,
  SideMenuItemConfig,
} from "../../utils/configSideMenu";
import { useUser } from "../../store/UserProvider";
import { UserRole, CognitoClaim } from "../../models/userModels";
import compass_logo from "../../assets/svg/compass_title_logo_black.svg";
import toggle_side_menu_button from "../../assets/svg/toggle_side_menu_button.svg";

const BackgroundContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background-color: white;
`;

const CompassLogoContainer = styled(IconContainer)<{ display: boolean }>`
  height: auto;
  display: ${({ display }): string => (display ? "block" : "none")};
  margin-top: 12px;
`;

const ToggleSideMenuContainer = styled(IconContainer)<{ rotate?: boolean }>`
  cursor: pointer;
  transform: ${({ rotate }): string => (rotate ? "rotate(180deg)" : "none")};

  &:hover {
    transform: ${({ rotate }): string =>
      rotate ? "scale(1.1) rotate(180deg)" : "scale(1.1) rotate(0deg)"};
  }
`;

const SideMenuTitle = styled.div<{ display: boolean }>`
  margin-left: 20px;
  padding: 5px;
  border-radius: 10px;
  display: ${({ display }): string => (display ? "block" : "none")};
`;

export const SideMenu = ({ show, setShow }: SideMenuProps): JSX.Element => {
  const { user } = useUser();
  const userRole = user?.[CognitoClaim.ROLE] as UserRole | undefined;

  const filterItemsByRole = (
    items: SideMenuItemConfig[]
  ): SideMenuItemConfig[] => {
    return items.filter((item) => {
      if (!item.excludedRoles || !userRole) return true;
      return !item.excludedRoles.includes(userRole);
    });
  };

  const filteredMainItems = useMemo(
    () => filterItemsByRole(SIDEMENU_ITEMS),
    [userRole]
  );
  const filteredBottomItems = useMemo(
    () => filterItemsByRole(BOTTOM_SIDEMENU_ITEMS),
    [userRole]
  );

  const handleClick = (): void => {
    setShow(!show);
  };

  return (
    <BackgroundContainer>
      <RowContainer ai={"center"} jc={show ? "space-between" : "center"}>
        <CompassLogoContainer size={150} br={1} display={show}>
          <Icon src={compass_logo} />
        </CompassLogoContainer>

        <ToggleSideMenuContainer
          size={45}
          br={1}
          onClick={handleClick}
          rotate={!show}
        >
          <Icon src={toggle_side_menu_button} />
        </ToggleSideMenuContainer>
      </RowContainer>
      <RowContainer>
        <SideMenuTitle display={show}>
          <CompassParagraph color={greyText}>MAIN MENU</CompassParagraph>
        </SideMenuTitle>
      </RowContainer>
      {filteredMainItems.map((item, index) => (
        <RowContainer
          jc={show ? "flex-start" : "center"}
          ai="center"
          key={index}
          mt={1}
          mb={1}
        >
          <SideMenuItem
            title={item.title}
            icon={item.icon}
            iconDark={item.iconDark || item.icon}
            showText={show}
            navigation={item.navigation}
            isActive={item.isActive}
            iconSize={item.iconSize}
          />
        </RowContainer>
      ))}
      <RowContainer flex={"1"} ai="flex-end">
        <SideMenuTitle display={show}>
          <CompassParagraph color={greyText}>PRODUCTS</CompassParagraph>
        </SideMenuTitle>
      </RowContainer>

      {filteredBottomItems.map((item, index) => (
        <RowContainer
          jc={show ? "flex-start" : "center"}
          ai="center"
          key={index}
          mt={1}
          mb={index === filteredBottomItems.length - 1 ? 25 : 1}
        >
          <SideMenuItem
            title={item.title}
            icon={item.icon}
            iconDark={item.icon}
            showText={show}
            navigation={item.navigation}
            isActive={item.isActive}
          />
        </RowContainer>
      ))}
    </BackgroundContainer>
  );
};

type SideMenuProps = {
  show: boolean;
  setShow: (show: boolean) => void;
};
